import {
  resolveDailyTargets,
  toLimits,
  toMinTargets,
  isMinOnlyTarget,
  type DayTargets,
} from '@/kidney/dailyTargets'
import { overLimitKeys } from '@/kidney/dayPerformance'
import { getFluidLimitByStage, isFluidRestricted } from '@/kidney/platform'
import { dayIronCoverage } from '@/lib/nutrients'
import type {
  DiaryEntry,
  DailyLimits,
  DailySummary,
  DayPerformance,
  KidneyProfile,
  MealTime,
  TrackedNutrientKey,
} from '@/types'

// Both helpers moved to @/kidney/platform (the shared kidney modules import
// them above their IMPORTS-END marker); re-exported so existing importers of
// '@/diary/summary' keep working.
export { getFluidLimitByStage, isFluidRestricted }

/**
 * LIVE targets for the profile as it is now (4.34). Past days must NOT use
 * this — they read the day's own snapshot via the diary context
 * (`targetsForDate`) or `effectiveTargetsForDate`.
 */
export function resolveTargets(profile?: KidneyProfile, isPremium = false): DayTargets | null {
  return resolveDailyTargets({ kidneyProfile: profile ?? null, isPremium })
}

/**
 * Core nutrients whose daily target is a floor only (4.32 min-only). Their
 * "limit" from resolveLimits IS the floor, so any %-of-limit bar must treat
 * passing 100% as the goal, never as "over" (mobile getDailyLimits parity).
 */
export function minOnlyCoreNutrients(
  profile?: KidneyProfile,
  isPremium = false,
): ('sodium' | 'potassium' | 'phosphorus' | 'protein')[] {
  return minOnlyCoreOf(resolveTargets(profile, isPremium))
}

/** Same, for a day's already-resolved targets (past days). */
export function minOnlyCoreOf(targets: DayTargets | null): ('sodium' | 'potassium' | 'phosphorus' | 'protein')[] {
  if (!targets) return []
  return (['sodium', 'potassium', 'phosphorus', 'protein'] as const).filter((k) => isMinOnlyTarget(targets, k))
}

/** LIVE limits (today). Past days go through toLimits(targetsForDate(date)). */
export function resolveLimits(profile?: KidneyProfile, isPremium = false): DailyLimits | null {
  const targets = resolveTargets(profile, isPremium)
  if (!targets) return null
  return toLimits(targets)
}

/**
 * Every tracked total keyed like DayTargets.values, rounded to 0.1 (what W2 persists).
 * 4.39: iron is the known subtotal at 2 decimals, omitted when no entry has a value.
 */
export function trackedTotalsOf(totals: DailySummary['totals']): Partial<Record<TrackedNutrientKey, number>> {
  const r = (v: number | undefined) => Math.round(((v || 0) + Number.EPSILON) * 10) / 10
  return {
    sodium: r(totals.sodiumMg), potassium: r(totals.potassiumMg), phosphorus: r(totals.phosphorusMg),
    protein: r(totals.proteinG), fluid: r(totals.fluidMl),
    calories: r(totals.calories), carbs: r(totals.carbsG), fat: r(totals.fatG),
    magnesium: r(totals.magnesiumMg), calcium: r(totals.calciumMg), vitaminK: r(totals.vitaminKMcg),
    ...(totals.ironMg !== undefined ? { iron: Math.round((totals.ironMg + Number.EPSILON) * 100) / 100 } : {}),
    oxalate: r(totals.oxalateMg), sugars: r(totals.sugarsG), fiber: r(totals.fiberG),
    saturatedFat: r(totals.saturatedFatG),
  }
}

const MEAL_TYPES: MealTime[] = ['breakfast', 'lunch', 'dinner', 'snack']

/**
 * Pure summary computation — mirrors DiaryContext's useMemo.
 *
 * 4.34: the caller passes the EFFECTIVE targets for `date` (today → live, a
 * past day → its snapshot or the backfilled estimate), never today's profile.
 */
export function computeSummary(
  date: string,
  entries: DiaryEntry[],
  targets: DayTargets | null,
): DailySummary | null {
  if (!targets) return null
  const limits = toLimits(targets)
  const floors = toMinTargets(targets)

  const totals = {
    sodiumMg: 0,
    potassiumMg: 0,
    phosphorusMg: 0,
    proteinG: 0,
    fluidMl: 0,
    calories: 0,
    carbsG: 0,
    sugarsG: 0,
    fiberG: 0,
    fatG: 0,
    saturatedFatG: 0,
    magnesiumMg: 0,
    calciumMg: 0,
    vitaminKMcg: 0,
    oxalateMg: 0,
  }
  const mealCounts: Record<MealTime, number> = {
    breakfast: 0,
    lunch: 0,
    dinner: 0,
    snack: 0,
  }

  for (const e of entries) {
    const n = e.nutrients || ({} as DiaryEntry['nutrients'])
    totals.sodiumMg += n.sodiumMg || 0
    totals.potassiumMg += n.potassiumMg || 0
    totals.phosphorusMg += n.phosphorusMg || 0
    totals.proteinG += n.proteinG || 0
    totals.fluidMl += e.fluidMl || 0
    totals.calories += n.calories || 0
    totals.carbsG += n.carbsG || 0
    totals.sugarsG += n.sugarsG || 0
    totals.fiberG += n.fiberG || 0
    totals.fatG += n.fatG || 0
    totals.saturatedFatG += n.saturatedFatG || 0
    totals.magnesiumMg += n.magnesiumMg || 0
    totals.calciumMg += n.calciumMg || 0
    totals.vitaminKMcg += n.vitaminKMcg || 0
    totals.oxalateMg += n.oxalateMg || 0

    const isPureFluid =
      e.isFluid &&
      !(n.sodiumMg || n.potassiumMg || n.phosphorusMg || n.proteinG)
    if (!isPureFluid && MEAL_TYPES.includes(e.mealType)) {
      mealCounts[e.mealType] += 1
    }
  }

  // 4.39 iron: unknown is not 0. The total is the KNOWN subtotal (2 decimals), present
  // only when at least one entry has a value; coverage counts the entries with nutrients
  // (pure fluids / Apple Health water are not counted). Mirrors mobile DiaryContext.
  const ironCov = dayIronCoverage(entries)
  const ironMg = ironCov.known > 0 ? ironCov.value : undefined

  const pctOf = (val: number | undefined, lim?: number) =>
    lim && lim > 0 ? ((val || 0) / lim) * 100 : undefined

  const percentages: Record<string, number | undefined> = {
    sodium: pctOf(totals.sodiumMg, limits.sodiumMg),
    potassium: pctOf(totals.potassiumMg, limits.potassiumMg),
    phosphorus: pctOf(totals.phosphorusMg, limits.phosphorusMg),
    protein: pctOf(totals.proteinG, limits.proteinG),
    fluid: pctOf(totals.fluidMl, limits.fluidMl),
    calories: pctOf(totals.calories, limits.calories),
    carbs: pctOf(totals.carbsG, limits.carbsG),
    fat: pctOf(totals.fatG, limits.fatG),
    magnesium: pctOf(totals.magnesiumMg, limits.magnesiumMg),
    calcium: pctOf(totals.calciumMg, limits.calciumMg),
    vitaminK: pctOf(totals.vitaminKMcg, limits.vitaminKMcg),
    iron: pctOf(ironMg, limits.ironMg),
    oxalate: pctOf(totals.oxalateMg, limits.oxalateMg),
    sugars: pctOf(totals.sugarsG, limits.sugarsG),
    fiber: pctOf(totals.fiberG, limits.fiberG),
    saturatedFat: pctOf(totals.saturatedFatG, limits.saturatedFatG),
  }

  // Mobile DiaryContext counts core nutrients PLUS any optional nutrient the
  // user has set a limit for (fluid excluded). This feeds the performance dot
  // written to the shared dailySummaries doc — diverging here makes phone and
  // web fight over the same doc.
  // A nutrient whose only bound is a minimum is a goal — passing 100% is
  // success, so it never joins the over-limit list. A range (min AND max)
  // warns against its max and carries its floor in minTargets (4.32 parity).
  const overLimitNutrients: string[] = []
  const minTargetNutrients: string[] = []
  const minTargets: NonNullable<DailySummary['minTargets']> = {}
  const minTargetsMet: TrackedNutrientKey[] = []

  const totalFor: Record<TrackedNutrientKey, number> = {
    sodium: totals.sodiumMg, potassium: totals.potassiumMg,
    phosphorus: totals.phosphorusMg, protein: totals.proteinG, fluid: totals.fluidMl,
    calories: totals.calories, carbs: totals.carbsG, fat: totals.fatG,
    magnesium: totals.magnesiumMg, calcium: totals.calciumMg,
    vitaminK: totals.vitaminKMcg, iron: ironMg ?? 0, oxalate: totals.oxalateMg,
    sugars: totals.sugarsG, fiber: totals.fiberG, saturatedFat: totals.saturatedFatG,
  }

  const warnKeys: TrackedNutrientKey[] = ['sodium', 'potassium', 'phosphorus', 'protein', 'calories', 'carbs', 'fat', 'magnesium', 'calcium', 'vitaminK', 'iron', 'oxalate', 'sugars', 'fiber', 'saturatedFat']
  // Ceilings exceeded — the SAME classifier the week-strip dot uses, so the
  // diary and the stored day can never disagree (4.34 dayPerformance).
  overLimitNutrients.push(...overLimitKeys(totalFor, targets))
  for (const key of warnKeys) {
    if (targets.values[key] && isMinOnlyTarget(targets, key)) minTargetNutrients.push(key)
  }
  // Floors, for every nutrient that has one (min-only or range)
  for (const key of [...warnKeys, 'fluid'] as TrackedNutrientKey[]) {
    const min = floors[key]
    if (min === undefined) continue
    minTargets[key] = min
    if (totalFor[key] >= min) minTargetsMet.push(key)
  }

  return {
    date,
    totals: ironMg !== undefined ? { ...totals, ironMg } : totals,
    limits,
    percentages,
    mealCounts,
    hasOverLimit: overLimitNutrients.length > 0,
    overLimitNutrients,
    minTargetNutrients,
    minTargets,
    minTargetsMet,
    coverage: { iron: ironCov },
  }
}

/** Performance dot: none (no food) / green (0 over) / yellow (1) / red (2+). */
export function dayPerformance(
  entryCount: number,
  overLimitCount: number,
): DayPerformance {
  if (entryCount === 0) return 'none'
  if (overLimitCount === 0) return 'green'
  if (overLimitCount === 1) return 'yellow'
  return 'red'
}
